"use client";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Logo from "./logo";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import header from "@/constants/header.constant";
import { useEffect, useRef, useState } from "react";
import { MenuType, SubMenuType } from "@/types/header.type";
import useDrawer from "@/stores/drawer";
import { Swiper, SwiperSlide } from "swiper/react";
import routes from "@/app.route";
import Announcement from "./announcement";
import { usePathname, useRouter } from "next/navigation";
import useSession from "@/hooks/session";
import Image from "next/image";
import config from "@/app.config";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import useCart from "@/stores/cart";
import Search from "./search";
import SparkleEffect from "./sparkle-effect";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

export default function Header() {
  const pathname = usePathname();
  const { items } = useCart();
  const [menu, setMenu] = useState<MenuType | null>(null);
  const [subMenu, setSubMenu] = useState<SubMenuType | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const matches = useMediaQuery("(min-width:1024px)");
  const { drawerState, toggleDrawerState } = useDrawer();
  const { customer } = useSession();
  const router = useRouter();

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setMenu(null);
      setSubMenu(null);
    }, 200);
  };

  const ItemCard = (val: {
    title: string;
    url: string;
    image: string | null;
  }) => (
    <SparkleEffect>
      <div
        className="block bg-zinc-100 cursor-pointer hover:bg-zinc-50"
        onClick={() => {
          toggleDrawerState();
          router.push(`${config.app.url}/${val.url}`);
          setMenu(null);
          setSubMenu(null);
        }}
      >
        {val.image ? (
          <Image
            className="w-full"
            src={`${config.app.apiBaseUrl}/${val.image}`}
            alt={val.title}
            width={200}
            height={200}
          />
        ) : (
          <div className="bg-zinc-100 rounded h-[200px] w-full"></div>
        )}
        <div className="px-4 pb-4 text-center truncate sm:text-sm text-xs">
          {val.title}
        </div>
      </div>
    </SparkleEffect>
  );

  useEffect(() => {
    if (menu && menu.nodes) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [menu]);

  return (
    <>
      <header className="relative z-40">
        {matches && <Announcement />}
        <div className="bg-white border-b border-zinc-200">
          <div className="w-full max-w-8xl mx-auto px-2 md:px-4 h-[60px] flex items-center">
            <div className="flex flex-1 justify-between items-center gap-4 h-full">
              <div className="flex items-center gap-x-2 md:gap-x-12 h-full">
                {!matches && (
                  <Tooltip title="Menu">
                    <IconButton
                      aria-label="Open navigation menu"
                      color="inherit"
                      onClick={toggleDrawerState}
                    >
                      <MenuRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <figure>
                  <Logo className="w-48" />
                </figure>
                {matches && (
                  <div className="flex items-center gap-x-8 h-full">
                    {header.map((value, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          if (value.url) {
                            router.push(`${config.app.url}/${value.url}`);
                          }
                        }}
                        onMouseEnter={() => {
                          if (hideTimeoutRef.current) {
                            clearTimeout(hideTimeoutRef.current);
                            hideTimeoutRef.current = null;
                          }

                          setMenu(value);

                          if (value.nodes) {
                            setSubMenu(value.nodes[0]);
                          }
                        }}
                        onMouseLeave={handleMouseLeave}
                        className={`cursor-pointer border-b-2 h-full flex items-center text-sm ${value.title == menu?.title ? "border-primary" : "border-transparent"}`}
                      >
                        {value.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center md:gap-2">
                <Search />
                <SparkleEffect>
                  <Tooltip title="Cart">
                    <IconButton
                      href={routes.cart.url}
                      aria-label={`View cart with ${items.length} items`}
                    >
                      <Badge badgeContent={items.length} color="primary">
                        <ShoppingCartOutlinedIcon fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </SparkleEffect>
                <SparkleEffect>
                  <Tooltip title={customer ? "Profile" : "Login"}>
                    <IconButton
                      aria-label={
                        customer ? "Open user profile" : "Login to your account"
                      }
                      href={
                        customer
                          ? routes.userProfile.url
                          : `${routes.login.url}` +
                            (pathname != routes.login.url
                              ? `?ret=${pathname}`
                              : "")
                      }
                    >
                      <PersonOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </SparkleEffect>
              </div>
            </div>
          </div>
        </div>
      </header>

      {menu && menu.nodes && (
        <>
          <div
            onMouseEnter={() => {
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
            }}
            onMouseLeave={handleMouseLeave}
            className="border-t border-zinc-200 text-sm bg-white fixed top-[100px] left-0 right-0 h-[calc(75vh-100px)] max-h-[calc(75vh-100px)] overflow-y-scroll shadow-xl z-40"
          >
            <div className="max-w-7xl mx-auto px-4 relative">
              <div className="absolute -right-8 top-3">
                <IconButton onClick={handleMouseLeave}>
                  <CloseOutlinedIcon />
                </IconButton>
              </div>
              {subMenu && subMenu.nodes ? (
                <div className="grid grid-cols-8 py-8 gap-12">
                  <div className="col-span-2">
                    {menu.nodes.map((value, index) => (
                      <div
                        onClick={() => {
                          setMenu(null);
                          setSubMenu(null);
                          router.push(`${config.app.url}/${value.url}`);
                        }}
                        key={index}
                        onMouseEnter={() => setSubMenu(value)}
                        className={`cursor-pointer flex items-center h-[60px] gap-3 hover:bg-zinc-100 px-2 py-1.5 ${value.title == subMenu.title ? "bg-zinc-100" : ""}`}
                      >
                        {value.image ? (
                          <Image
                            width={50}
                            height={50}
                            src={`${config.app.apiBaseUrl}/${value.image}`}
                            alt={value.title}
                          />
                        ) : (
                          <div className="size-[50px] bg-zinc-100 rounded"></div>
                        )}
                        <div>{value.title}</div>
                      </div>
                    ))}
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-4 gap-2">
                      {subMenu.nodes.map((value, index) =>
                        value.nodes ? (
                          <div key={index} className="mt-2 col-span-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <div className="font-medium">{value.title}</div>
                              <IconButton
                                size="small"
                                sx={{
                                  backgroundColor: (theme) =>
                                    theme.palette.primary.main,
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: (theme) =>
                                      theme.palette.primary.dark,
                                  },
                                }}
                                onClick={() => {
                                  router.push(`${config.app.url}/${value.url}`);
                                  setMenu(null);
                                  setSubMenu(null);
                                }}
                              >
                                <KeyboardArrowRightRoundedIcon fontSize="small" />
                              </IconButton>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {value.nodes.map((val, i) => (
                                <ItemCard key={`${index}-${i}`} {...val} />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <ItemCard key={index} {...value} />
                        ),
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-4 py-8 gap-4">
                  {menu.nodes.map((value, index) => (
                    <ItemCard key={index} {...value} />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="bg-black/50 left-0 right-0 top-0 bottom-0 fixed z-20"></div>
        </>
      )}

      {!matches && (
        <Drawer open={drawerState} onClose={toggleDrawerState}>
          <Box sx={{ width: "100vw" }} role="presentation">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              px={2}
              py={1.5}
            >
              <Logo className="w-48" />
              <IconButton onClick={toggleDrawerState}>
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
            <Divider />
            <List>
              {header.map((value, index) =>
                value.nodes ? (
                  <Accordion
                    key={index}
                    disableGutters
                    elevation={0}
                    sx={{
                      "&:before": {
                        display: "none",
                      },
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        component="span"
                        sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                      >
                        {value.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {value.nodes.map((val, i) =>
                        val.nodes ? (
                          <Accordion
                            key={`${index}-${i}`}
                            disableGutters
                            elevation={0}
                            sx={{
                              "&:before": {
                                display: "none",
                              },
                              boxShadow: "none",
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.9rem",
                                  fontWeight: 500,
                                  pl: 1,
                                }}
                              >
                                {val.title}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{ py: 2.5, px: 0 }}
                              className="bg-zinc-50"
                            >
                              <Swiper
                                spaceBetween={8}
                                slidesPerView="auto"
                                slidesOffsetBefore={20}
                                slidesOffsetAfter={20}
                              >
                                {val.nodes.map((v, k) => (
                                  <SwiperSlide
                                    key={`${index}-${i}-${k}`}
                                    className="bg-zinc-100 max-w-[200px]"
                                  >
                                    <ItemCard {...v} />
                                  </SwiperSlide>
                                ))}
                              </Swiper>
                            </AccordionDetails>
                          </Accordion>
                        ) : (
                          <ListItem
                            key={`${index}-${i}`}
                            disablePadding
                            disableGutters
                            dense
                          >
                            <ListItemButton
                              onClick={() => {
                                toggleDrawerState();
                                router.push(`${config.app.url}/${val.url}`);
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                                  >
                                    {val.title}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ),
                      )}
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        toggleDrawerState();
                        router.push(`${config.app.url}/${value.url}`);
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            component="span"
                            sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                          >
                            {value.title}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ),
              )}
            </List>
          </Box>
        </Drawer>
      )}
    </>
  );
}
